import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { css } from '@emotion/core'
import * as FS from '@fullstory/browser'

import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'
import Spinner from '@divvy-web/skylab.spinner'
import { TOAST_TYPE_BASIC, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import PageViewWrapper from '@sputnik/components/PageViewWrapper/PageViewWrapper'
import TimeoutWarningModal from '@sputnik/components/TimeoutWarningModal'
import { useBillSessionInfo } from '@sputnik/hooks/useBillSessionInfo'
import { useKeepNeoSessionAlive } from '@sputnik/hooks/useKeepNeoSessionAlive'
import GlobalTopNav from '@sputnik/remotes/GlobalTopNav/GlobalTopNav'
import {
  CreditApplicationStatus,
  MAJOR_ACCOUNT_CHANGE,
  PAGE_NAME_STRINGS,
  PATHNAME_CONSTS,
} from '@sputnik/resources/constants'
import { getBillApolloClient } from '@sputnik/utils/billApolloUtils'
import { matchEmail } from '@sputnik/utils/dataUtils'
import { logInfo } from '@sputnik/utils/loggerUtils'

import DelayedLoader from './DelayedLoader'
import { GetApplicationStatuses } from './gql/GetApplicationStatuses.gql'
import StatusErrorTile from './Status/StatusErrorTile/StatusErrorTile'

const DashboardApplication = lazy(() => import('./Status/DashboardApplication'))

const getApplicationIds = (applications) => {
  return applications.reduce((appIdAcc, app) => {
    appIdAcc.push(app?.salesforceCreditId)
    return appIdAcc
  }, [])
}

const Dashboard = () => {
  const showToast = useToast()
  const { email } = useAuth()
  const navigate = useNavigate()
  const { search, state } = useLocation()
  const [showedLoginToast, setShowedLoginToast] = useState(false)
  const [bdcOrgId, setBdcOrgId] = useState('')
  const [billApolloClient, setBillApolloClient] = useState(null)
  const [shouldTopNavBeVisible, setShouldTopNavBeVisible] = useState(false)
  const [currentCompanyName, setCurrentCompanyName] = useState('')

  useDidMount(() => {
    window.sessionStorage.removeItem('submissionPendingUrl')
  })

  const { data, error, loading, refetch } = useQuery(GetApplicationStatuses, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const applications = useMemo(
    () =>
      [].concat(data?.creditApplications ?? []).sort((app1, app2) => {
        if (app1.statusUpdatedAt && !app2.statusUpdatedAt) {
          return -1
        }
        if (!app1.statusUpdatedAt && app2.statusUpdatedAt) {
          return 1
        }
        if (app1.statusUpdatedAt > app2.statusUpdatedAt) {
          return -1
        }
        if (app1.statusUpdatedAt < app2.statusUpdatedAt) {
          return 1
        }

        return 0
      }),
    [data?.creditApplications],
  )

  const referrerQueryParams = new URLSearchParams(state?.referrerSearch || '')
  const redirectToParam = referrerQueryParams.get('redirect_to')?.toLowerCase()
  const creditIdParam = referrerQueryParams.get('credit_id')?.toLowerCase()
  const bdcOrgIdParam = referrerQueryParams.get('bdc_org_id')?.toLowerCase()
  const { PAGE_DASHBOARD } = PAGE_NAME_STRINGS
  const shouldStayOnDashboard = redirectToParam === PAGE_DASHBOARD

  const applicationCount = applications?.length
  const onlyHasOneCreditApp = applicationCount === 1
  const firstCreditApp = applications?.[0]

  const hasApplications = applicationCount > 0
  const canStartNewApplication = !!data?.salesforceContactExists || !hasApplications
  const shouldRedirectToSignup = !shouldStayOnDashboard && !error && !data?.salesforceContactExists && !hasApplications

  const appIdsArray = useMemo(
    () => (hasApplications ? getApplicationIds(applications) : []),
    [applications, hasApplications],
  )

  useEffect(() => {
    if (applications) {
      const bdcOrgId = applications[0]?.bdcOrgId
      setBdcOrgId(bdcOrgId)
      setBillApolloClient(getBillApolloClient(bdcOrgId))
    }
  }, [applications])

  const { data: sessionInfoData } = useBillSessionInfo(billApolloClient)

  useEffect(() => {
    if (sessionInfoData) {
      setShouldTopNavBeVisible(sessionInfoData?.ossType === 'bdcUp' || sessionInfoData?.isConsole)
      setCurrentCompanyName(sessionInfoData?.organizationName || applications[0]?.businessInfo?.legalBusinessName)
    }
  }, [applications, sessionInfoData])

  useKeepNeoSessionAlive(bdcOrgId)

  useEffect(() => {
    if (!loading && !showedLoginToast) {
      showToast(
        TOAST_TYPE_BASIC,
        <FormattedMessage
          defaultMessage='You are signed in as {email}'
          id='sputnik.Dashboard__RMGhLe'
          values={{ email }}
        />,
        {
          closeMsg: (
            <span css={dismissButtonCss}>
              <FormattedMessage
                defaultMessage='Dismiss'
                id='sputnik.Dashboard__TDaF6J'
              />
            </span>
          ),
          dataTestId: 'signedin-email-toast',
        },
      )

      setShowedLoginToast(true)

      if (FS.isInitialized()) {
        FS.identify(email)
        FS.event('Dashboard', { sf_credit_id_strs: appIdsArray })
      }

      logInfo('dashboardPageLoad', {
        attributes: {
          ...(hasApplications && { creditAppIds: appIdsArray }),
          applicationCount,
          result: `Dashboard page loaded with ${applicationCount} application(s)`,
        },
        eventName: 'PageLoad',
      })
    }
  }, [applicationCount, hasApplications, appIdsArray, email, loading, showToast, showedLoginToast])

  if (loading) return <Spinner fullPage />

  if (shouldRedirectToSignup) {
    navigate({
      pathname: PATHNAME_CONSTS.GOOD_FIT_SURVEY_PATH,
    })
  }

  const matchingBdcApp =
    bdcOrgIdParam && applications.find((creditApp) => creditApp.bdcOrgId?.toLowerCase() === bdcOrgIdParam)

  const matchingCreditApp =
    matchingBdcApp ||
    (creditIdParam && applications.find((creditApp) => creditApp.salesforceCreditId?.toLowerCase() === creditIdParam))

  const creditAppToRedirectTo = onlyHasOneCreditApp ? firstCreditApp : matchingCreditApp

  const applicationNotStartedOrInProgress = [
    CreditApplicationStatus.NOT_STARTED,
    CreditApplicationStatus.IN_PROGRESS,
  ].includes(creditAppToRedirectTo?.status)

  const isLggedInAsAwaitedSigner = () => {
    if (onlyHasOneCreditApp) {
      return (
        CreditApplicationStatus.AWAITING_SIGNATURE === firstCreditApp?.status &&
        matchEmail(email, firstCreditApp?.authorizedSigner?.email)
      )
    }
    return undefined
  }

  const shouldRedirectToAppSection =
    !shouldStayOnDashboard &&
    state?.referrer === PATHNAME_CONSTS.AUTH_PATH &&
    creditAppToRedirectTo &&
    (applicationNotStartedOrInProgress || !!isLggedInAsAwaitedSigner())

  if (shouldRedirectToAppSection) {
    const appId = creditAppToRedirectTo?.salesforceCreditId
    const isMac = creditAppToRedirectTo?.recordType === MAJOR_ACCOUNT_CHANGE
    const isLatestAppVersion = creditAppToRedirectTo?.appVersion === 3
    const defaultPath = isLatestAppVersion ? PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH : 'business-info'
    const path = isMac
      ? isLatestAppVersion
        ? PATHNAME_CONSTS.REVIEW_PATH
        : PATHNAME_CONSTS.REVIEW_AND_SIGN_PATH
      : defaultPath

    logInfo('dashboardActiveCreditAppBypass', {
      attributes: {
        creditAppId: appId,
        isBdcOrg: !!matchingBdcApp,
        result: `Bypassing dashboard to go to the credit application ${appId}`,
      },
      eventName: 'PageLoad',
    })

    const applicationPath = isLatestAppVersion ? `/app/${appId}/${path}` : `/app/${appId}/section/${path}`

    navigate(
      { pathname: applicationPath, search: search ?? '' },
      {
        state: {
          ...state,
          autoRedirectIndex: path === defaultPath ? 0 : undefined,
          referrer: 'dashboard',
        },
      },
    )

    return <Spinner fullPage />
  }

  return (
    <>
      {shouldTopNavBeVisible && (
        <GlobalTopNav
          apolloClient={billApolloClient}
          bdcOrgId={bdcOrgId}
          companyName={currentCompanyName}
          isConsole={sessionInfoData?.isConsole || false}
        />
      )}
      <PageViewWrapper
        bdcOrgId={bdcOrgId}
        canStartNewApplication={canStartNewApplication}
        pageName={PAGE_DASHBOARD}
      >
        <Suspense fallback={<DelayedLoader centered />}>
          {(!hasApplications || error) && <StatusErrorTile refetch={refetch} />}
          {hasApplications && (
            <div
              className='applications-container'
              css={appsContainerCss}
            >
              {applications.map((creditApp) => (
                <DashboardApplication
                  key={creditApp?.salesforceCreditId}
                  creditApp={creditApp}
                  refetch={refetch}
                />
              ))}
            </div>
          )}
          <TimeoutWarningModal pageName={PAGE_DASHBOARD} />
        </Suspense>
      </PageViewWrapper>
    </>
  )
}

const dismissButtonCss = css`
  color: var(--tri-color-icon-info-inverse);
`

const appsContainerCss = ({ mq }) => css`
  display: flex;
  flex-direction: column;
  gap: var(--tri-space-1200);

  ${mq.mediumMaxWidth({ gap: 'var(--tri-space-500)' })}
`

export default Dashboard
